import type { ReactNode } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { useAppContext } from "../hooks/useAppContext";
import { demoMode } from "../lib/config";
import { Seo } from "../lib/seo";
import { AccountMenu } from "./account-menu";
import { IncidentBanner } from "./IncidentBanner";
import { Icon, type IconName } from "./icons";
import { Pill } from "./dashboard";
import { WorkspaceSwitcher } from "./workspace-switcher";

/** ODIN brand lockup (winged-helmet mark + wordmark). */
export function Brand(): JSX.Element {
  return (
    <div className="ax-brand">
      <img src="/brand/odin/odin-mark-128.png" alt="" className="ax-brand__mark-img" aria-hidden="true" />
      <span className="ax-brand__word">ODIN</span>
    </div>
  );
}

const NAV: ReadonlyArray<{ to: string; label: string; icon: IconName; end?: boolean }> = [
  { to: "/app", label: "Overview", icon: "overview", end: true },
  { to: "/app/balder", label: "AI Command Centre", icon: "ai" },
  { to: "/app/servers", label: "Server", icon: "server" },
  { to: "/app/members", label: "Members", icon: "members" },
  { to: "/app/roles", label: "Roles", icon: "roles" },
  { to: "/app/moderation", label: "Moderation", icon: "moderation" },
  { to: "/app/tickets", label: "Tickets", icon: "tickets" },
  { to: "/app/automations", label: "Automations", icon: "automations" },
  { to: "/app/content", label: "Content & Feeds", icon: "content" },
  { to: "/app/analytics", label: "Analytics", icon: "analytics" },
  { to: "/app/logs", label: "Logs", icon: "logs" },
  { to: "/app/integrations", label: "Integrations", icon: "integrations" },
  { to: "/app/bot-config", label: "Bot Configuration", icon: "botConfig" },
  { to: "/app/billing", label: "Billing", icon: "billing", end: true },
  { to: "/app/support", label: "Support", icon: "help" },
  { to: "/app/settings", label: "Settings", icon: "settings" },
];

export function SideNav(): JSX.Element {
  return (
    <nav className="ax-nav" aria-label="Primary">
      {NAV.map((item) => {
        const IconCmp = Icon[item.icon];
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => (isActive ? "ax-nav__item active" : "ax-nav__item")}
          >
            <span className="ax-nav__icon">
              <IconCmp />
            </span>
            {item.label}
          </NavLink>
        );
      })}
    </nav>
  );
}

/**
 * The sidebar's billing entry point (Part 15: "Billing & Plan").
 *
 * Extracted from `AppLayout` so the label can be asserted without standing up
 * the entire shell. It is deliberately the only place the full "Billing & plan"
 * wording appears: the nav item above it stays the single word "Billing"
 * because two links to the same page with the same name is a worse experience
 * than one short name and one full one — a screen reader reading the sidebar
 * would otherwise announce the same destination twice, identically, with no way
 * to tell which is which.
 */
export function PlanLink(): JSX.Element {
  return (
    <NavLink to="/app/billing" className="ax-plan" style={{ textDecoration: "none" }}>
      <div className="ax-plan__name">Billing &amp; plan</div>
      <div className="ax-plan__meta">View plan &amp; usage</div>
    </NavLink>
  );
}

/**
 * Persistent demo banner. Shown across every /app screen when the dashboard is
 * built in demo mode (`PUBLIC_DEMO_MODE=true`) so it is unmistakable that this is a
 * live preview — not a connected, production workspace.
 */
export function DemoBanner(): JSX.Element | null {
  if (!demoMode) return null;
  return (
    <div className="ax-demobar" role="note">
      <span className="ax-demobar__tag">Demo</span>
      <span className="ax-demobar__text">
        You’re exploring a live preview of ODIN. No real Discord server is connected, so panels stay
        empty on purpose — nothing here touches a real server.
      </span>
      <Link to="/login" className="ax-demobar__cta">
        Start free →
      </Link>
    </div>
  );
}

export function AppLayout(): JSX.Element {
  return (
    <div className="ax-shell">
      <Seo title="ODIN" noindex />
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <aside className="ax-sidebar">
        <Brand />
        <SideNav />
        <PlanLink />
      </aside>
      <div className="ax-main">
        <TopBar />
        <DemoBanner />
        <IncidentBanner />
        <main id="main-content" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function TopBar(): JSX.Element {
  const { guildName } = useAppContext();

  return (
    <header className="ax-topbar">
      <WorkspaceSwitcher />
      <Pill tone="neutral" label={guildName ? "Bot connected" : "No bot"} />

      <span className="ax-topbar__spacer" />

      <span className="ax-search" aria-hidden="true">
        <Icon.search />
        Search ODIN…
        <span className="ax-search__kbd">⌘K</span>
      </span>
      <NavLink to="/app/inbox" className="ax-iconbtn" aria-label="Notifications">
        <Icon.bell />
      </NavLink>
      <NavLink to="/app/support" className="ax-iconbtn" aria-label="Help">
        <Icon.help />
      </NavLink>
      <AccountMenu />
    </header>
  );
}

export function PageHeader({
  title,
  children,
}: {
  title: string;
  children?: ReactNode;
}): JSX.Element {
  return (
    <div className="page-header">
      <h1>{title}</h1>
      {children}
    </div>
  );
}
